
/**
 * Route the Home-style back control returns to (the Plans list).
 * @constant {string}
 */
export const PLANS_LIST_ROUTE = "/plan-manager/plans";


/**
 * Plan setup / completion status labels shown in the Plan Status column.
 * @readonly
 * @enum {string}
 */
export const PlanStatus = Object.freeze({
    NOT_STARTED: "Not Started",
    STARTED: "Started",
    COMPLETED: "Completed",
});


/**
 * Adjudication platform tabs. Only one plan table is shown at a time based on the active tab.
 * @readonly
 * @enum {string}
 */
export const PlanPlatformTab = Object.freeze({
    JAVELINA: "JAVELINA",
    HRP: "HRP",
});


/**
 * Drill-down route paths reached from Create / Edit on the Networks list.
 * @readonly
 * @type {{ NETWORKS_CONFIG: string }}
 */
export const NETWORK_MANAGER_ROUTE_PATHS = Object.freeze({
    NETWORKS_CONFIG: "/plan-manager/networks/config",
});


/**
 * Config tab identifiers.
 * @readonly
 * @enum {string}
 */
export const ConfigTabId = Object.freeze({
    NETWORKS: "networks",
    PRE_ENROLLMENT: "preEnrollment",
    PLAN_AFFILIATION: "planAffiliation",
});


/**
 * Route the back-link and header breadcrumb return to (the Networks list).
 * @constant {string}
 */
export const NETWORKS_LIST_ROUTE = "/plan-manager/networks";


/**
 * Tab ids for the right-hand panel.
 * @readonly
 * @enum {string}
 */
export const SiteCustomizationTabId = Object.freeze({
    DESKTOP_PREVIEW: "desktop-preview",
    CLIENT: "client",
});


/**
 * Whether the Desktop preview tab is shown. Mirrors `VUE_APP_SHOW_PREVIEW`
 * in the legacy app; a build-time flag with a safe default for this shell.
 * @constant {boolean}
 */
export const SHOW_PREVIEW = true;


/**
 * Stub toast copy for this page (no API calls in this shell).
 * @readonly
 */
export const SITE_CUSTOMIZATION_TOAST_MESSAGES = Object.freeze({
    SAVE_SUCCESS: "You have successfully submitted your work.",
    SAVE_ERROR: "Please fix the errors below, and try saving again.",
    PUBLISH_SUCCESS: "The pre-enrollment site has been published.",
    PUBLISH_ERROR: "Failed to publish the pre-enrollment site.",
    UNPUBLISH_SUCCESS: "The pre-enrollment site has been unpublished.",
    UNPUBLISH_ERROR: "Failed to unpublish the pre-enrollment site.",
});


export const PLAN_MANAGER_QUERY_KEYS = {
    plansAll: ["planManager", "plansAll"],
    defaultNotificationConfig: ["planManager", "defaultNotificationConfig"],
    settings: ["planManager", "settings"],
    clientConfigs: ({ shouldGetHrpPlans = false, isCustom = false } = {}) => [
        "planManager",
        "clientConfigs",
        shouldGetHrpPlans,
        isCustom,
    ],
    networkSeriesOptions: ["planManager", "networkSeriesOptions"],
    clientConfig: (clientName) => ["planManager", "clientConfig", clientName],
    medicalPlan: (clientId, planId, platformTab) => [
        "planManager",
        "medicalPlan",
        platformTab,
        clientId,
        planId,
    ],
};


export const PLAN_MANAGER_LONG_FETCH_QUERY_OPTIONS = {
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
};


export const PLAN_MANAGER_INVALIDATION_KEYS = [
    PLAN_MANAGER_QUERY_KEYS.plansAll,
    PLAN_MANAGER_QUERY_KEYS.defaultNotificationConfig,
];


/**
 * Central invalidation entry point for Plan Manager long-fetch retrieval queries.
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 */
export const invalidatePlanManagerRetrievalQueries = (queryClient) =>
    queryClient.invalidateQueries({ queryKey: PLAN_MANAGER_INVALIDATION_KEYS });
